import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

const nav = [
  { to: '/',            label: 'Dashboard',    icon: '📊', roles: ['admin','transport_officer','manager','mechanic','driver','foreman','accountant'] },
  { to: '/vehicles',    label: 'Vehicles',     icon: '🚗', roles: ['admin','transport_officer','manager','mechanic','foreman'] },
  { to: '/drivers',     label: 'Drivers',      icon: '👤', roles: ['admin','transport_officer','manager'] },
  { to: '/assignments', label: 'Assignments',  icon: '🔗', roles: ['admin','transport_officer','manager'] },
  { to: '/repairs',     label: 'Repairs',      icon: '🔧', roles: ['admin','transport_officer','manager','mechanic','driver','foreman'] },
  { to: '/approvals',   label: 'Approvals',    icon: '🖊️', roles: ['admin','foreman','manager'] },
  { to: '/invoices',    label: 'Invoices',     icon: '🧾', roles: ['admin','transport_officer','manager','foreman','accountant'] },
  { to: '/accidents',   label: 'Accidents',    icon: '💥', roles: ['admin','transport_officer','manager','foreman','accountant'] },
  { to: '/fuel',        label: 'Fuel',         icon: '⛽', roles: ['admin','transport_officer','manager'] },
  { to: '/compliance',  label: 'Compliance',   icon: '✅', roles: ['admin','transport_officer','manager'] },
  { to: '/renewals',    label: 'Renewals',     icon: '🔄', roles: ['admin','transport_officer','manager'] },
  { to: '/inventory',   label: 'Inventory',    icon: '📦', roles: ['admin','transport_officer'] },
  { to: '/garages',     label: 'Suppliers',    icon: '🏭', roles: ['admin','transport_officer','manager'] },
  { to: '/users',       label: 'Users',        icon: '👥', roles: ['admin'] },
  { to: '/settings',    label: 'Settings',     icon: '⚙️', roles: ['admin'] },
];

export default function Layout({ children }) {
  const { user, logout, can } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const handleLogout = () => { logout(); navigate('/login'); };
  const links = nav.filter(n => can(...n.roles));

  return (
    <div className="flex h-screen overflow-hidden text-ink-text">
      {/* Sidebar */}
      <aside className={`
        fixed inset-y-0 left-0 z-50 w-64 bg-[#1B1640] border-r border-ink-line flex flex-col transform transition-transform duration-200
        ${open ? 'translate-x-0' : '-translate-x-full'} md:relative md:translate-x-0
      `}>
        {/* Logo */}
        <div className="flex items-center gap-3 px-6 py-5 border-b border-ink-line">
          <span className="w-9 h-9 rounded-xl grid place-items-center text-lg bg-[rgba(139,92,246,.12)] ring-1 ring-[rgba(139,92,246,.3)] shadow-[0_0_20px_-6px_rgba(139,92,246,.8)]">🚛</span>
          <div>
            <p className="font-display font-bold text-sm leading-tight tracking-tight">Transport</p>
            <p className="text-[11px] uppercase tracking-widest text-ink-faint leading-tight mt-0.5">Fleet Control</p>
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto py-4 px-3">
          {links.map(({ to, label, icon }) => (
            <Link
              key={to} to={to}
              onClick={() => setOpen(false)}
              className={`
                flex items-center gap-3 px-3 py-2.5 rounded-lg mb-1 text-sm font-medium transition-colors
                ${location.pathname === to || (to !== '/' && location.pathname.startsWith(to))
                  ? 'nav-lit'
                  : 'text-ink-muted hover:text-ink-text hover:bg-white/[.05]'}
              `}
            >
              <span className="text-base">{icon}</span>
              {label}
            </Link>
          ))}
        </nav>

        {/* User info */}
        <div className="px-4 py-4 border-t border-ink-line">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 rounded-full bg-[rgba(139,92,246,.15)] ring-1 ring-[rgba(139,92,246,.35)] text-[#8B5CF6] flex items-center justify-center text-sm font-bold">
              {user?.name?.[0]}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{user?.name}</p>
              <p className="text-xs text-ink-faint capitalize">{user?.role?.replace('_',' ')}</p>
            </div>
          </div>
          <button onClick={handleLogout}
            className="w-full text-left text-xs text-ink-faint hover:text-[#8B5CF6] transition-colors px-2">
            Sign out →
          </button>
        </div>
      </aside>

      {/* Mobile overlay */}
      {open && <div className="fixed inset-0 bg-black/50 z-40 md:hidden" onClick={() => setOpen(false)} />}

      {/* Main */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top bar */}
        <header className="bg-[#1B1640]/70 backdrop-blur border-b border-ink-line px-4 py-3 flex items-center gap-4">
          <button onClick={() => setOpen(!open)} className="md:hidden text-ink-muted hover:text-[#8B5CF6]">
            ☰
          </button>
          <div className="flex-1">
            <h1 className="text-sm font-display font-semibold tracking-tight text-ink-text">
              {links.find(n => n.to === location.pathname || (n.to !== '/' && location.pathname.startsWith(n.to)))?.label || 'Dashboard'}
            </h1>
          </div>
          <span className="text-xs bg-[rgba(139,92,246,.12)] text-[#8B5CF6] ring-1 ring-[rgba(139,92,246,.28)] px-2.5 py-1 rounded-full capitalize">
            {user?.role?.replace('_',' ')}
          </span>
        </header>

        {/* Content */}
        <main className="light-shaft flex-1 overflow-y-auto p-6">
          {children}
        </main>
      </div>
    </div>
  );
}
